import { useState } from 'react';
import { LoaderIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import { DialogTitle } from '@radix-ui/react-dialog';
import { AllStoreType } from 'components/ImageEditor/store/imageEditor.type';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { useImageEditorStore } from 'components/ImageEditor/ImageEditorStoreProvider';
import {
  ImageGenService,
  ImageToolTypeEnum,
} from 'features/text-to-image-generator/services/image-gen.api';

export default function SaveImageModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const { imageUrl } = useImageEditorStore<AllStoreType>((state) => state);

  const saveImage = useMutation({
    mutationKey: ['save-generated-image'],
    mutationFn: ImageGenService.saveImageEditorGeneratedImage,
    onSuccess: () => {
      setIsOpen(false);
      toast.success('Image saved successfully');
    },
  });

  const handleSubmit = (e: any) => {
    e.preventDefault();

    saveImage.mutateAsync({
      image_name: fileName,
      image_url: imageUrl as any,
      tool_type: ImageToolTypeEnum.OBJECT_REMOVAL,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger
        asChild
        onClick={() => {
          setFileName('Untitled 1');
          setIsOpen(true);
        }}
      >
        <Button
          variant={'customSecondary'}
          className="bg-secondary"
          size={'sm'}
        >
          Save
        </Button>
      </DialogTrigger>
      <DialogContent className="text-text-primary">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-primary">
            Save as
          </DialogTitle>
        </DialogHeader>
        <form>
          <div className="flex flex-col gap-2">
            <label htmlFor="fileName">Image name</label>
            <Input
              name="fileName"
              value={fileName}
              onChange={(e) => {
                const { value } = e.target;
                setFileName(value);
              }}
              type="text"
              min={2}
            />
          </div>
          <DialogFooter className="mt-4">
            <Button
              onClick={() => {
                setFileName('Untitled 1');
                setIsOpen(false);
              }}
              variant={'customSecondary'}
              size={'sm'}
              className="bg-secondary"
              type="button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant={'customSecondary'}
              size={'sm'}
              disabled={saveImage.isPending}
              className="bg-secondary"
            >
              {saveImage.isPending && (
                <div className="animate-spin">
                  <LoaderIcon size={20} />
                </div>
              )}
              {saveImage.isPending ? 'Saving' : 'Save & Continue'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
